import { useEffect, useMemo, useState, useRef } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

type NotificationEventType =
  | "entry"
  | "exit"
  | "quick_patrol"
  | "double_hit"
  | "checkpoint_rule"
  | "checkpoint_scan";

type NotificationRule = {
  id: string;
  eventType: NotificationEventType;
  startMinutes: number;
  endMinutes: number;
  email: boolean;
  sms: boolean;
};

type NotificationSettingsData = {
  timezone: string;
  email: string;
  phone?: string;
  smsOptIn: boolean;
  rules: NotificationRule[];
  updatedAt?: number | null;
};

const EVENT_OPTIONS: Array<{ value: NotificationEventType; label: string; description: string; color: string }> = [
  { value: "entry",           label: "Site Entry",       description: "Vehicle or guard enters a monitored site.",          color: "#22c55e" },
  { value: "exit",            label: "Site Exit",        description: "Vehicle or guard exits a monitored site.",           color: "#f59e0b" },
  { value: "quick_patrol",    label: "Quick Patrol",     description: "Quick patrol analytics flag a patrol completion.",   color: "#3b82f6" },
  { value: "double_hit",      label: "Double Hit",       description: "A double-hit event is detected for a guard.",       color: "#ef4444" },
  { value: "checkpoint_rule", label: "Rule Violation",   description: "A checkpoint scan violates its configured rule.",    color: "#ef4444" },
  { value: "checkpoint_scan", label: "Checkpoint Scan",  description: "A checkpoint is scanned at a site.",                color: "#eab308" },
];
const SMS_DISABLED_TOOLTIP_TEXT = "SMS notifications are currently Disabled";
const SMS_DISABLED_PHONE_TEXT = "SMS notifications are Disabled";

function minutesToTimeInput(minutes: number) {
  const safe = Math.max(0, Math.min(1439, Math.floor(minutes)));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

function timeInputToMinutes(value: string) {
  const match = String(value || "").match(/^(\d{2}):(\d{2})$/);
  if (!match) return 0;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
  return Math.max(0, Math.min(1439, hours * 60 + minutes));
}

function createRule(eventType: NotificationEventType): NotificationRule {
  return {
    id: `${eventType}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    eventType,
    startMinutes: 8 * 60,
    endMinutes: 17 * 60,
    email: true,
    sms: false,
  };
}

export default function NotificationPreferencesCard() {
  const settings = useQuery((api as any).notifications.getMyNotificationSettings) as
    | NotificationSettingsData
    | undefined;
  const saveSettings = useMutation((api as any).notifications.saveMyNotificationSettings);
  const isLoading = settings === undefined;

  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<Set<NotificationEventType>>(new Set());
  const didInitialExpand = useRef(false);

  useEffect(() => {
    if (!settings || isDirty) return;
    setRules((settings.rules || []).map((rule) => ({ ...rule, sms: false })));
  }, [settings, isDirty]);

  useEffect(() => {
    if (!settings || didInitialExpand.current) return;
    const withRules = new Set<NotificationEventType>();
    for (const rule of settings.rules || []) {
      withRules.add(rule.eventType as NotificationEventType);
    }
    if (withRules.size > 0) setExpandedEvents(withRules);
    didInitialExpand.current = true;
  }, [settings]);

  const rulesByEvent = useMemo(() => {
    const grouped = new Map<NotificationEventType, NotificationRule[]>();
    for (const option of EVENT_OPTIONS) grouped.set(option.value, []);
    for (const rule of rules) {
      const list = grouped.get(rule.eventType);
      if (list) list.push(rule);
    }
    for (const list of grouped.values()) {
      list.sort((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes);
    }
    return grouped;
  }, [rules]);

  const email = (settings?.email || "").trim();

  const markDirty = () => {
    setIsDirty(true);
    setError(null);
    setSuccess(null);
  };

  const updateRule = (ruleId: string, patch: Partial<NotificationRule>) => {
    markDirty();
    setRules((current) =>
      current.map((rule) => (rule.id === ruleId ? { ...rule, ...patch } : rule))
    );
  };

  const addRule = (eventType: NotificationEventType) => {
    markDirty();
    setRules((current) => [...current, createRule(eventType)]);
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      next.add(eventType);
      return next;
    });
  };

  const removeRule = (ruleId: string) => {
    markDirty();
    setRules((current) => current.filter((rule) => rule.id !== ruleId));
  };

  const toggleEvent = (eventType: NotificationEventType) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventType)) next.delete(eventType);
      else next.add(eventType);
      return next;
    });
  };

  const onSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);
      await saveSettings({
        smsOptIn: false,
        rules: rules.map((rule) => ({
          ...rule,
          sms: false,
          startMinutes: Math.max(0, Math.min(1439, Math.floor(rule.startMinutes))),
          endMinutes: Math.max(0, Math.min(1439, Math.floor(rule.endMinutes))),
        })),
      });
      setIsDirty(false);
      setSuccess("Routing config saved");
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : "Failed to save notification settings");
    } finally {
      setIsSaving(false);
    }
  };

  const activeRuleCount = rules.filter((r) => r.email || r.sms).length;

  return (
    <section className="op-routing">
      <div className="op-routing__header">
        <div className="op-routing__stat">
          <span className="op-routing__stat-num">{activeRuleCount}</span>
          <span className="op-routing__stat-label">Active Rules</span>
        </div>
        <div className="op-routing__stat">
          <span className="op-routing__stat-num">{rules.filter((r) => r.email).length}</span>
          <span className="op-routing__stat-label">Email Routes</span>
        </div>
        <div className="op-routing__stat">
          <span className="op-routing__stat-num">{rules.filter((r) => r.sms).length}</span>
          <span className="op-routing__stat-label">SMS Routes</span>
        </div>
      </div>

      <div className="op-routing__meta">
        <div className="op-routing__meta-row">
          <span className="op-routing__meta-k">Email</span>
          <span className="op-routing__meta-v">{email || "Not set"}</span>
        </div>
        <div className="op-routing__meta-row">
          <span className="op-routing__meta-k">Phone</span>
          <span className="op-routing__meta-v">{SMS_DISABLED_PHONE_TEXT}</span>
        </div>
      </div>

      {isLoading && (
        <div className="op-routing__loading">
          <span className="op-spinner" />
          Loading routing configuration&hellip;
        </div>
      )}

      <div className="op-events">
        {EVENT_OPTIONS.map((option) => {
          const eventRules = rulesByEvent.get(option.value) || [];
          const isOpen = expandedEvents.has(option.value);

          return (
            <div key={option.value} className={`op-event${isOpen ? " op-event--open" : ""}`}>
              <button type="button" className="op-event__head" onClick={() => toggleEvent(option.value)}>
                <span className="op-event__indicator" style={{ background: option.color }} />
                <svg className="op-event__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
                <div className="op-event__label">
                  <span className="op-event__name">{option.label}</span>
                  <span className="op-event__count">
                    {eventRules.length === 0 ? "No windows" : `${eventRules.length} window${eventRules.length > 1 ? "s" : ""}`}
                  </span>
                </div>
                <button
                  type="button"
                  className="op-event__add"
                  onClick={(e) => { e.stopPropagation(); addRule(option.value); }}
                  disabled={isLoading}
                  title="Add delivery window"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </button>
              </button>

              {isOpen && (
                <div className="op-event__body">
                  {eventRules.length === 0 ? (
                    <div className="op-event__empty">
                      <span>No delivery windows.</span>
                      <button type="button" onClick={() => addRule(option.value)} disabled={isLoading}>
                        Add first window
                      </button>
                    </div>
                  ) : (
                    eventRules.map((rule) => (
                      <div key={rule.id} className="op-rule">
                        <div className="op-rule__time">
                          <input
                            className="op-rule__time-input"
                            type="time"
                            value={minutesToTimeInput(rule.startMinutes)}
                            disabled={isLoading}
                            onChange={(e) => updateRule(rule.id, { startMinutes: timeInputToMinutes(e.target.value) })}
                          />
                          <span className="op-rule__arrow">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M5 12h14M12 5l7 7-7 7"/>
                            </svg>
                          </span>
                          <input
                            className="op-rule__time-input"
                            type="time"
                            value={minutesToTimeInput(rule.endMinutes)}
                            disabled={isLoading}
                            onChange={(e) => updateRule(rule.id, { endMinutes: timeInputToMinutes(e.target.value) })}
                          />
                        </div>
                        <div className="op-rule__controls">
                          <button
                            type="button"
                            className={`op-pill${rule.email ? " op-pill--on" : ""}`}
                            onClick={() => updateRule(rule.id, { email: !rule.email })}
                            disabled={isLoading}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                              <path d="M22 6l-10 7L2 6"/>
                            </svg>
                            Email
                          </button>
                          <span title={SMS_DISABLED_TOOLTIP_TEXT}>
                            <button
                              type="button"
                              className="op-pill"
                              disabled
                              aria-disabled="true"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                              </svg>
                              SMS
                            </button>
                          </span>
                          <button
                            type="button"
                            className="op-rule__delete"
                            onClick={() => removeRule(rule.id)}
                            disabled={isLoading}
                            title="Remove window"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(error || success) && (
        <div className={`op-toast op-toast--inline${error ? " op-toast--error" : " op-toast--ok"}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            {error ? (
              <><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></>
            ) : (
              <><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></>
            )}
          </svg>
          <span>{error || success}</span>
        </div>
      )}

      <div className="op-routing__footer">
        <button
          type="button"
          className="op-actions__btn op-actions__btn--primary op-actions__btn--full"
          onClick={onSave}
          disabled={isSaving || isLoading}
        >
          {isSaving ? (
            <><span className="op-spinner" />Saving</>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                <path d="M17 21v-8H7v8M7 3v5h8"/>
              </svg>
              Save Routing Config
            </>
          )}
        </button>
      </div>
    </section>
  );
}
